import {
  useMemo,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import cx from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faTrash } from '@fortawesome/pro-regular-svg-icons';

import {
  useAppSelector,
  useAppDispatch,
  mapSelector,
  MarkerType,
  FileType,
  updateMarker,
} from '../../../../store';

import { apiBaseURL } from '../../../../config';
import { FileUploader } from './FileUploader';
import { Icon } from '../../../markers/Icon';

import './MarkerUpload.scss';

export function MarkerUpload({
  selectedMarker,
  setSelectedMarker,
  markers,
  setMarkers,
} : {
  selectedMarker?:MarkerType;
  setSelectedMarker:any;
  markers:MarkerType[];
  setMarkers:(markers:MarkerType[])=>void;
}) {

  const dispatch = useAppDispatch();
  const iMap = useAppSelector(mapSelector);

  const [width, height] = useMemo(() => {
    if (!selectedMarker || !selectedMarker.size) return [57, 57, 1];
    const [ width, height ] = selectedMarker.size.split('x').map((n) => Number(n));
    return [width, height, width / height];
  }, [selectedMarker]);

  return (
    <label>
      <span>Markers</span>
      <div className="image-uploader markers-uploader">
        <FileUploader
          onFilesUpload={(files:FileType[]) => {
            const asMarkers = files.map((f) => {
              return {
                ...f,
                id: uuidv4(),
                position_order: 0,
                base64: f.base64!,
                size: `${width}x${height}`,
                kind: "upload" as "upload",
              };
            });
            setMarkers(markers.concat(asMarkers));

            // Fix width to keep aspect ratio for new uploaded markers
            asMarkers.forEach((marker) => {
              const image = new Image();
              image.onload = (i: any) => {
                if (!i.target) return;
                // Calculate new image aspect ratio
                const height = Number(marker.size.split('x')[1]);
                const aspectRatio = i.target.width / i.target.height;
                if (aspectRatio) {
                  // Keep height but change width for aspect ratio
                  const width = Math.round(height! * aspectRatio);
                  dispatch(updateMarker({...marker, size: `${width}x${height}` }));
                }
              };
              image.src = marker.base64!;
            });
          }}
          label=""
          multiple={true}
          maxSize={500_000} // 500 KB
          acceptedTypes={[
            'image/png',
            'image/jpg',
            'image/jpeg',
            'image/webp',
            'image/svg+xml',
          ]}
        >
          <div className="images-container">
            <div
              className={cx('image-preview', {'active': 'default' === selectedMarker?.id})}
              onClickCapture={(e) => {
                e.preventDefault();
                setSelectedMarker({id: 'default'});
              }}
              style={{
                height: 57,
                width: 57,
              }}
            >
              <div title="Edit global default markers">
                <FontAwesomeIcon
                  icon={faGear}
                  className="image-action"
                  onClickCapture={(e) => {
                    e.preventDefault();
                    window?.open(
                      `${apiBaseURL}/client/orders/${iMap.order.id}/interactive_maps/admin`,
                      '_blank',
                    );
                  }}
                />
              </div>

              <img
                src={ iMap.defaultMarkers.base64 || iMap.defaultMarkers.url }
                alt="marker"
                style={{
                  height: 57,
                  width: 'auto',
                }}
              />
            </div>

            { markers.map((marker, index) => (
              <div
                key={marker.id}
                className={cx('image-preview', {'active': marker.id.toString() === selectedMarker?.id?.toString()})}
                onClickCapture={(e) => {
                  e.preventDefault();
                  setSelectedMarker(marker);
                }}
                style={{
                  height: 57,
                  width: 57,
                }}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  className="image-action"
                  onClickCapture={(e) => {
                    e.preventDefault();
                    setMarkers(markers.toSpliced(index, 1));
                  }}
                />
                <Icon
                  marker={marker}
                  number="1"
                  letter="A"
                />
              </div>
            ))}
          </div>
        </FileUploader>
        <i style={{ color: "green" }}>File types: svg, png, jpg, webp</i>
      </div>
    </label>
  );
}
